// components/FirmwareFlasher.tsx
// 펌웨어 한 개를 GitHub 에서 받아 USB(COM 포트)로 보드에 설치하는 패널.
// 전체 설치: .merged.bin 을 0x0 에 쓴다. 보드에 저장된 Wi-Fi 설정, API 키 등은 지워진다.
// fileUrls 는 앞에서부터 차례로 시도한다 (새 위치 → 옛 위치).
import React, { useEffect, useRef, useState } from 'react';
import { ESPLoader, Transport } from 'esptool-js';

const BAUD = 460800;

interface Props {
  title: string;
  fileUrls: string[];
  autoStart?: boolean; // true 면 창이 열리자마자 COM 포트 선택 창을 띄운다 (사진을 누른 직후에 쓴다)
}

const FirmwareFlasher: React.FC<Props> = ({ title, fileUrls, autoStart }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const logEnd = useRef<HTMLDivElement>(null);

  const supported = typeof navigator !== 'undefined' && !!(navigator as any).serial;

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev.slice(-200), msg]);
    setTimeout(() => logEnd.current?.scrollIntoView({ behavior: 'smooth' }), 0);
  };

  const download = async (): Promise<Uint8Array> => {
    let lastErr = '';
    for (const url of fileUrls) {
      try {
        const res = await fetch(url, { cache: 'no-cache' });
        if (!res.ok) { lastErr = `HTTP ${res.status}`; continue; }
        const data = new Uint8Array(await res.arrayBuffer());
        if (data.length < 100 * 1024) { lastErr = '받은 파일이 너무 작습니다'; continue; }
        return data;
      } catch (e: any) {
        lastErr = e?.message || String(e);
      }
    }
    throw new Error(`펌웨어를 받지 못했습니다 (${lastErr})`);
  };

  const start = async () => {
    const serial = (navigator as any).serial;
    if (!serial || busy) return;

    setError('');
    setLogs([]);
    setProgress(0);

    let port: any = null;
    let transport: Transport | null = null;
    try {
      // 포트 선택은 버튼을 누른 직후에 해야 브라우저가 허용한다 (다운로드보다 먼저)
      setStatus('COM 포트를 선택하세요');
      port = await serial.requestPort();
      setBusy(true);

      setStatus('GitHub 에서 펌웨어 받는 중...');
      addLog(`GitHub 에서 ${title} 펌웨어를 받습니다.`);
      const data = await download();
      addLog(`받기 완료 (${(data.length / 1024).toFixed(0)} KB)`);

      setStatus('보드에 연결하는 중... (자동으로 안 되면 BOOT 버튼을 누른 채 시도)');
      const terminal = {
        clean: () => {},
        writeLine: (d: string) => addLog(d),
        write: (d: string) => addLog(d),
      };
      transport = new Transport(port, false);
      const loader = new ESPLoader({ transport, baudrate: BAUD, terminal });
      const chip = await loader.main();
      addLog(`칩: ${chip}`);

      setStatus('설치 중... (창을 닫거나 USB 를 뽑지 마세요)');
      await loader.writeFlash({
        fileArray: [{ data, address: 0x0 }],
        flashMode: 'keep',
        flashFreq: 'keep',
        flashSize: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (_i: number, written: number, total: number) => {
          setProgress(Math.round((written / total) * 100));
        },
      });
      await loader.after('hard_reset');
      setProgress(100);
      setStatus('설치 완료! 보드가 재시작합니다. Wi-Fi 설정을 다시 입력하세요.');
      addLog('✅ 설치 완료');
    } catch (e: any) {
      if (e?.name === 'NotFoundError') {
        setStatus(''); // 포트 선택 취소
      } else {
        const raw = e?.message || String(e);
        // 포트를 못 여는 가장 흔한 이유는 다른 프로그램이 같은 포트를 쓰고 있는 것이다
        const msg = /Failed to open serial port/i.test(raw)
          ? 'COM 포트를 열 수 없습니다. 아두이노 시리얼 모니터나 다른 프로그램, 다른 브라우저 탭이 같은 포트를 쓰고 있지 않은지 확인하고 모두 닫은 뒤, USB 를 뽑았다 다시 꽂고 시도하세요.'
          : raw;
        setError(msg);
        setStatus('');
        addLog('❌ ' + raw);
      }
    } finally {
      if (transport) {
        try { await transport.disconnect(); } catch (_) { /* 이미 닫힘 */ }
      }
      setBusy(false);
    }
  };

  // 사진을 누른 직후에는 바로 COM 포트 선택 창을 띄운다 (브라우저는 클릭 직후에만 허용한다)
  useEffect(() => {
    if (autoStart) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', textAlign: 'left', minWidth: '280px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>{title} 펌웨어 설치 (USB)</div>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px' }}>
        PC 의 크롬/엣지에서 보드를 USB 로 연결하세요. 최신 펌웨어를 GitHub 에서 받아 바로 설치합니다.
        시리얼 모니터는 닫아 두세요.
        설치하면 보드의 Wi-Fi 설정이 초기화되므로 설치 후 다시 입력해야 합니다.
      </div>

      {!supported && (
        <div style={{ color: '#b91c1c', marginBottom: '10px' }}>이 브라우저는 Web Serial 을 지원하지 않습니다. (PC 의 Chrome/Edge 필요)</div>
      )}

      <button type="button" className="action-btn btn-primary" onClick={start} disabled={!supported || busy}>
        {busy ? `설치 중... ${progress}%` : 'COM 포트 선택하고 설치 시작'}
      </button>

      {(busy || progress > 0) && (
        <div style={{ marginTop: '10px', height: '10px', background: '#e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s' }} />
        </div>
      )}
      {status && <div style={{ marginTop: '10px', color: '#2563eb', fontSize: '0.9rem' }}>{status}</div>}
      {error && (
        <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', color: '#b91c1c', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}
      {logs.length > 0 && (
        <div style={{ marginTop: '10px', maxHeight: '140px', overflowY: 'auto', fontSize: '0.75rem', color: '#475569', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px' }}>
          {logs.map((l, i) => <div key={i}>{l}</div>)}
          <div ref={logEnd} />
        </div>
      )}
    </div>
  );
};

export default FirmwareFlasher;
